import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";
import { toCssPx } from "../../lib/appZoom";

export default function Tooltip({ 
  content, 
  children, 
  className = "", 
  align = "center" 
}) {
  const [visible, setVisible] = useState(false);
  const triggerRef = useRef(null);
  const isTouchRef = useRef(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, height: 0 });

  useEffect(() => {
    if (!visible) return;

    const updateCoords = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        // O rect vem em pixel real da tela; o balão é posicionado dentro do zoom do app.
        setCoords({
          top: toCssPx(rect.top + window.scrollY),
          left: toCssPx(rect.left + window.scrollX),
          width: toCssPx(rect.width),
          height: toCssPx(rect.height)
        });
      }
    };

    updateCoords();

    // Close the tooltip when scrolling or resizing
    const handleCloseEvent = () => {
      setVisible(false);
    };

    window.addEventListener("scroll", handleCloseEvent, true);
    window.addEventListener("resize", handleCloseEvent);

    const handleOutsideClick = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target)) {
        setVisible(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      window.removeEventListener("scroll", handleCloseEvent, true);
      window.removeEventListener("resize", handleCloseEvent);
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [visible]);

  const handleMouseEnter = () => {
    if (isTouchRef.current) return;
    setVisible(true);
  };

  const handleMouseLeave = () => {
    if (isTouchRef.current) return;
    setVisible(false);
  };

  const handleTouchStart = () => {
    isTouchRef.current = true;
  };

  const handleToggle = (e) => {
    e.stopPropagation();
    setVisible((prev) => !prev);
  };

  const renderTooltipContent = () => {
    if (!visible || !content) return null;

    const bubbleWidth = 208; // w-52 in px
    const margin = 12; // margin from screen edges
    const viewportWidth = typeof window !== "undefined" ? toCssPx(window.innerWidth) : 360;

    const centerX = coords.left + coords.width / 2;
    let leftPosition = centerX;

    // Shift bubble right if overflowing left edge
    if (centerX - bubbleWidth / 2 < margin) {
      leftPosition = margin + bubbleWidth / 2;
    }
    // Shift bubble left if overflowing right edge
    else if (centerX + bubbleWidth / 2 > viewportWidth - margin) {
      leftPosition = viewportWidth - margin - bubbleWidth / 2;
    }

    // Offset the arrow so it points directly to the center of the trigger
    const shift = centerX - leftPosition;

    const style = {
      position: "absolute",
      zIndex: 9999,
      top: `${coords.top - 8}px`, // 8px offset above trigger
      left: `${leftPosition}px`,
      transform: "translate(-50%, -100%)", // position above and center horizontally
    };

    return createPortal(
      <div 
        style={style}
        className="w-52 rounded-xl border border-border bg-popover/95 p-2.5 text-xs text-popover-foreground shadow-xl backdrop-blur-sm animate-in fade-in slide-in-from-bottom-1 duration-150 break-words whitespace-pre-line text-center pointer-events-none select-none"
      >
        {content}
        <div 
          className="absolute top-full border-4 border-transparent border-t-slate-900/95 dark:border-t-slate-900/98"
          style={{
            left: `calc(50% + ${shift}px)`,
            transform: "translateX(-50%)"
          }}
        />
      </div>,
      document.body
    );
  };

  return (
    <div 
      ref={triggerRef}
      className={cn("relative inline-block", className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onClick={handleToggle}
    >
      {children}
      {renderTooltipContent()}
    </div>
  );
}
